import {
  Button,
  Checkbox,
  Classes,
  Dialog,
  Divider,
  Tooltip,
} from '@blueprintjs/core';
import { loan_application } from '@axo/shared/data-access/types';
import styles from './AcceptedOfferDialog.module.css';
import { Bank } from '@axo/ui-content/features/Partners';
import type { BankCode } from '@axo/ui-content/features/Partners/Bank';
import { LoanQuoteTextField } from '../../QuoteDataForm/Fields/LoanQuoteTextField';
import { currencyFormatter } from '../../../../utils/formatCurrency';
import { formatDuration } from '../../../../utils/formatDuration';
import { useCallback, useMemo, useState } from 'react';
import { usePatchLoanQuote } from '../../../../../../../../libs/shared/data-access/hooks/src/queries/useLoanQuotes.ts';
import { LoanQuoteStatus } from '@axo/shared/data-access/types/loan_quote';
import { useFormContext } from 'react-hook-form';
import { LoanQuoteDataFormValues } from '../../QuoteDataForm/QuoteDataFormSchema';
import { classNames } from '../../../../utils/classNames';
import { IQuoteDetail } from '../../hooks/useBankPanelData.tsx';

type ValidationErrors = {
  Amount: Array<string>;
  Duration: Array<string>;
};

export function AcceptedOfferDialog({
  isOpen,
  onClose,
  application,
  item,
}: {
  isOpen: boolean;
  onClose(): void;
  application: loan_application.LoanApplication;
  item: IQuoteDetail;
}) {
  const currencyFormat = currencyFormatter(application.Currency);
  const [confirmedAmount, setConfirmedAmount] = useState<boolean>(false);
  const [confirmedDuration, setConfirmedDuration] = useState<boolean>(false);

  const validationEmptyState = useMemo<ValidationErrors>(
    () => ({ Amount: [], Duration: [] }),
    [],
  );

  const { mutateAsync: patchLoanQuote, isLoading } = usePatchLoanQuote();
  const { getValues, resetField, watch } =
    useFormContext<LoanQuoteDataFormValues>();

  const amount = watch(`${item.ID}.Amount`);
  // const duration = watch(`${item.ID}.Duration`);

  const validate = useCallback(() => {
    const newErrors: ValidationErrors = { Amount: [], Duration: [] };

    // if (
    //   amount &&
    //   parseFloat(amount.replace(/\./g, '')) <
    //     parseFloat(item.Lender.MinimumLoanAmount)
    // ) {
    //   newErrors.Amount.push(
    //     `Value can't be lower than Lender's minimum required amount: ${item.Lender.MinimumLoanAmount}`
    //   );
    // }

    if (
      item.LenderMaxPossibleAmount &&
      amount &&
      amount > item.LenderMaxPossibleAmount
    ) {
      newErrors.Amount.push(
        `Value can't exceed Lender's maximum allowed amount: ${item.LenderMaxPossibleAmount}`,
      );
    }

    // if (
    //   duration &&
    //   parseFloat(duration.replace(/\./g, '')) <
    //     parseFloat(item.Lender.MinimumLoanDuration)
    // ) {
    //   newErrors.Duration.push(
    //     `Value can't be lower than Lender's minimum required duration: ${item.Lender.MinimumLoanDuration}`
    //   );
    // }

    // if (
    //   duration &&
    //   parseFloat(duration.replace(/\./g, '')) >
    //     parseFloat(item.Lender.MaximumLoanDuration)
    // ) {
    //   newErrors.Duration.push(
    //     `Value can't exceed Lender's maximum allowed duration: ${item.Lender.MaximumLoanDuration}`
    //   );
    // }

    return newErrors;
  }, [
    amount,
    // duration,
    item.LenderMaxPossibleAmount,
    // item.Lender.MaximumLoanDuration,
    // item.Lender.MinimumLoanAmount,
    // item.Lender.MinimumLoanDuration,
  ]);

  const errors = useMemo(() => {
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      return validationErrors;
    } else {
      return validationEmptyState;
    }
  }, [validate, validationEmptyState]);

  async function updateOffer() {
    const quoteToPatch = getValues(item.ID);
    if (quoteToPatch) {
      await patchLoanQuote({
        loanQuoteID: item.ID,
        applicationID: application.ID,
        data: {
          Amount: parseFloat(quoteToPatch.Amount?.toString() || '') ?? 0,
          Duration: parseFloat(quoteToPatch.Duration?.toString() || '') ?? 0,
          Status:
            LoanQuoteStatus.OfferAcceptedPendingBankDisbursementDecisionReAccept,
          MarketCountry: application?.MarketCountry,
        },
      });
      resetForm();
      onClose();
    }
  }

  function resetForm() {
    setConfirmedAmount(false);
    setConfirmedDuration(false);
    resetField(`${item.ID}.Amount`);
    resetField(`${item.ID}.Duration`);
  }

  function closeForm() {
    resetForm();
    onClose();
  }

  const isAllowedToSubmit =
    confirmedAmount &&
    confirmedDuration &&
    !errors.Amount.length &&
    !errors.Duration.length;

  if (!item.LoanRequestID) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeForm}
      title="Edit accepted offer"
      icon="highlight"
    >
      <div className={styles.dialogBody}>
        <div className={styles.titleContainer}>
          You are currently editing an accepted offer from:
          <div className={styles.logoContainer}>
            <Bank
              code={item.Lender.ShortName as BankCode}
              name={item.Lender.Name}
              country={application.MarketCountry}
            />
          </div>
        </div>
        <Divider />
        <div className={styles.fieldContainer}>
          <strong className={styles.info}>Offer amount:</strong>
          <div className={classNames(styles.fieldDisplay, styles.currentLabel)}>
            <span>Currently accepted:</span>
            <span>
              {item.Amount ? currencyFormat(item.Amount.toString()) : '-'}
            </span>
          </div>
          <div className={styles.fieldDisplay}>
            <span>New amount:</span>
            <Tooltip
              disabled={!item.LenderMaxPossibleAmount}
              placement={'top'}
              content={`Maximum value must be ${item.LenderMaxPossibleAmount}`}
              openOnTargetFocus={false}
            >
              <LoanQuoteTextField
                applicationID={application.ID}
                loanQuoteID={item.ID}
                name={`${item.ID}.Amount`}
                errorMessage={
                  errors.Amount.length ? errors.Amount[0] : undefined
                }
              />
            </Tooltip>
            <Checkbox
              checked={confirmedAmount}
              onChange={() => setConfirmedAmount((x) => (x = !x))}
              label="Confirm"
              className={styles.checkbox}
            />
          </div>
        </div>

        <div className={styles.fieldContainer}>
          <strong className={styles.info}>Offer duration:</strong>
          <div className={classNames(styles.fieldDisplay, styles.currentLabel)}>
            <span>Currently accepted:</span>
            <span>{item.Duration ? formatDuration(item) : '-'}</span>
          </div>
          <div className={styles.fieldDisplay}>
            <span>New duration:</span>

            <LoanQuoteTextField
              applicationID={application.ID}
              loanQuoteID={item.ID}
              name={`${item.ID}.Duration`}
              errorMessage={
                errors.Duration.length ? errors.Duration[0] : undefined
              }
            />
            <Checkbox
              checked={confirmedDuration}
              onChange={() => setConfirmedDuration((x) => (x = !x))}
              label="Confirm"
              className={styles.checkbox}
            />
          </div>
        </div>
      </div>
      <Divider />
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={closeForm} icon="trash" aria-label="Close Dialog">
            Discard changes
          </Button>

          <Tooltip
            disabled={confirmedAmount && confirmedDuration}
            placement={'top'}
            content="Please confirm the changes before sending to bank."
            openOnTargetFocus={false}
          >
            <Button
              intent="primary"
              aria-label="Send to bank"
              icon="saved"
              loading={isLoading}
              onClick={updateOffer}
              disabled={!isAllowedToSubmit}
            >
              Send to bank
            </Button>
          </Tooltip>
        </div>
      </div>
    </Dialog>
  );
}
